// "Not sent" — a person checked the portal for a held claim and it is not there.
//
// A held claim is one where submit was pressed and no reference was captured, so
// it may or may not be at the payer. Returning it to the queue means a person or
// the skill will send it again, so the wrong answer here is a duplicate claim. The
// modal asks the biller to confirm they looked, and to say what they found; A leaves
// the reason on the claim as a comment. Never optimistic: the row moves only on 200.

import React, { useEffect, useRef, useState } from "react";
import { markNotSent } from "./api";
import { relativeTime } from "./ageing";
import useReturnFocus from "./useReturnFocus";
import "./Queue.css";

const MAX_REASON = 1000; // submission_queue_service.MAX_NOTE

export default function NotSentModal({
  practiceId,
  item,
  onClose,
  onStale,
  onDone,
}) {
  const [checked, setChecked] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const reasonRef = useRef(null);
  const checkRef = useRef(null);

  // The confirmation comes first in the form, so focus starts there.
  useReturnFocus(checkRef);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const valid = checked && reason.trim().length > 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = await markNotSent(practiceId, item.id, reason.trim());
      onDone(item, body);
    } catch (err) {
      setError(err.message);
      setBusy(false);
      // 409: the row changed under this page (someone else acted on it). Refresh
      // the list behind the modal so the row stops offering stale actions.
      if (err.status === 409 && onStale) onStale();
      setTimeout(() => reasonRef.current?.focus(), 0);
    }
  };

  return (
    <div className="sq-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="sq-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sq-notsent-title"
      >
        <div className="sq-modal-header">
          <h2 id="sq-notsent-title">Not sent — return to the queue</h2>
          <button
            className="sq-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="sq-modal-body">
          <div className="sq-modal-summary">
            <span>
              Claim <strong>#{item.claimId}</strong>
            </span>
            <span>DOS {item.dateOfService || "—"}</span>
            <span>{item.payerName || "—"}</span>
            {item.updatedAt && <span>Held {relativeTime(item.updatedAt)}</span>}
          </div>

          <div className="sq-message warn">
            <span>
              Submit was pressed on this claim and no reference was captured.
              Returning it to the queue means it will be sent again. If the
              claim is in the portal, close this and use “I submitted” with the
              portal’s reference instead. A claim sent in the last few minutes
              may not show in the portal yet — if it was held recently, wait and
              look again.
            </span>
          </div>

          <label className="sq-check">
            <input
              ref={checkRef}
              type="checkbox"
              checked={checked}
              disabled={busy}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>
              I looked for this claim in the portal and it is not there.
            </span>
          </label>

          <label className="sq-field">
            <span>What you checked</span>
            <textarea
              ref={reasonRef}
              rows={3}
              maxLength={MAX_REASON}
              value={reason}
              disabled={busy}
              placeholder="e.g. searched Claim.MD by claim id and DOS 9/12 — no claim"
              onChange={(e) => setReason(e.target.value)}
            />
            <small className="sq-field-hint">
              Saved on the claim. Claim ids and payer detail only — no patient
              names.
            </small>
          </label>

          {error && (
            <div className="sq-message error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="sq-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger btn-sm sq-danger"
            onClick={submit}
            disabled={!valid || busy}
          >
            {busy ? "Returning…" : `Return claim #${item.claimId} to the queue`}
          </button>
        </div>
      </div>
    </div>
  );
}
